import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import {
  Button,
  getTranslation,
  Icon,
  IconClose,
  Link,
  Loader,
  Modal,
  RSVPDeclined,
  TranslationProvider,
  focusTrap,
} from '@resy/ui-kitchen';
import {
  getPartyInviteDetails,
  postPartyInviteConfirm,
  postPartyInviteDecline,
  getVenue,
} from '../../../../../ui-kitchen/src/api/api';
import { venueTransform } from '../../../../../ui-kitchen/src/api/resyApiTransformer';
import { selectIsAuthenticated } from '../../../../../ui-kitchen/src/reducers/authTokenSlice';

import Download from '../Download';
import DownloadAppleButton from '../../svg/DownloadAppleButton.svg';
import RSVPConfirmed from './RSVPConfirmed';
import RSVPNotFound from './RSVPNotFound';
import RSVPPending from './RSVPPending';
import { Logger } from '../../../../../ui-kitchen/src/utils';

import './rsvp.scss';

export const CONFIRMED = 1;
export const PENDING = 2;
export const NOTFOUND = 3;
export const DECLINED = 4;

const testId = 'guest_invite';
const SERVICE_TYPES = {
  5: 'Breakfast',
  1: 'Brunch',
  3: 'Lunch',
  2: 'Dinner',
};
const modalStyles = {
  overlay: {
    alignItems: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    display: 'flex',
    zIndex: '10',
  },
  content: {
    backgroundColor: '#fff',
    border: 'none',
    borderRadius: '10px',
    boxShadow: '0 10px 50px 0 rgba(0, 0, 0, 0.1)',
    height: 'fit-content',
    margin: '0 auto',
    maxWidth: '400px',
    padding: '30px',
    textAlign: 'center',
    top: '40%',
  },
};

const rsvpPropTypes = {
  $routeParams: PropTypes.shape({
    token: PropTypes.string,
  }).isRequired,
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func,
    matchMedia: PropTypes.shape({
      xs: PropTypes.bool,
      sm: PropTypes.bool,
      md: PropTypes.bool,
      lg: PropTypes.bool,
      ipadLandscape: PropTypes.bool,
      xl: PropTypes.bool,
    }).isRequired,
    metadata: PropTypes.shape({
      bodyClasses: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.string,
      image_twitter: PropTypes.string,
      image_facebook: PropTypes.string,
      title: PropTypes.string,
    }).isRequired,
    nav: PropTypes.shape(),
    prerender: PropTypes.shape({
      responseCode: PropTypes.string,
    }),
    titleBase: PropTypes.string,
  }).isRequired,
  $translate: PropTypes.func.isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
};

const RSVP = ({
  $routeParams,
  $rootScope,
  $translate,
  Analytics,
  isAuthenticated,
}) => {
  const [state, setState] = React.useState({
    alert: false,
    confirmProcessing: false,
    declineProcessing: false,
    isConfirmed: undefined,
    isDeclined: undefined,
    isLoading: undefined,
    isNotFound: undefined,
    isPending: undefined,
    showAuthModal: false,
    showDeclineModal: false,
    showDownloadBanner: false,
    showDownloadButtons: false,
    reservation: {},
    serviceType: null,
    venueData: undefined,
    venueImage: undefined,
  });

  const {
    alert,
    confirmProcessing,
    declineProcessing,
    isConfirmed,
    isDeclined,
    isLoading,
    isNotFound,
    isPending,
    showAuthModal,
    showDeclineModal,
    showDownloadBanner,
    showDownloadButtons,
    reservation,
    serviceType,
    venueData,
    venueImage,
  } = state;

  const renderAlert = () => (
    <div className="ResyInviteRsvp__message alert">
      <TranslationProvider translationKey="invite_error_processing_line_1">
        <span className="ResyInviteRsvp__message--bold">There was a problem processing your request.</span>
      </TranslationProvider>
      <TranslationProvider translationKey="invite_error_processing_line_2">
        <span>Please double check the link is correct.</span>
      </TranslationProvider>
    </div>
  );

  const authorizedConfirm = async (inviteToken) => {
    try {
      await postPartyInviteConfirm({ host_attendee_token: inviteToken });

      // The invite was accepted successfully. Render the confirmed state from
      // the reservation data we already have instead of reloading the page.
      // A full reload rebuilt the screen purely from a fresh invite fetch,
      // which raced a just-registered user's session and rendered the
      // "Invite Not Found" screen despite a successful acceptance (CON-18705).
      setState((prevState) => ({
        ...prevState,
        reservation: {
          ...prevState.reservation,
          party: {
            ...prevState.reservation.party,
            current_user_confirmed: true,
          },
        },
        alert: false,
        confirmProcessing: false,
        isLoading: false,
        isConfirmed: true,
        isDeclined: false,
        isNotFound: false,
        isPending: false,
        showAuthModal: false,
        showDownloadBanner: true,
        showDownloadButtons: false,
      }));
    } catch (error) {
      Logger.error(`[UserInvite] error confirming reservation: ${error?.data?.message}`);
      setState((prevState) => ({
        ...prevState,
        confirmProcessing: false,
        isLoading: false,
        alert: true,
      }));
    }
  };

  const generatePageTitle = (venue) => {
    $translate('titles.invite.rsvpForVenue')
      .then(
        (translationString) => {
          $rootScope.metadata.title = `${translationString} ${venue}`;
        }
      );
  };

  const injectReservationData = (data) => {
    const reservationIsInPast = moment(data.reservation.date_time_iso).isBefore();
    $rootScope.prerender.responseCode = reservationIsInPast ? '404' : '200';

    if (reservationIsInPast) {
      setState((prevState) => ({
        ...prevState,
        isNotFound: true,
        reservation: data,
        showDownloadButtons: true,
      }));
      return;
    }

    const reservationServiceType = data.reservation.event?.event_name
      ? data.reservation.event.event_name
      : SERVICE_TYPES[data.reservation.service_type_id];
    const localDate = moment(data.reservation.date_time_iso)
      .format('dddd, MMM D');

    // eslint-disable-next-line no-param-reassign
    data.reservation.localDate = localDate;
    if (data.party.current_user_is_host || data.party.current_user_confirmed) {
      setState((prevState) => ({
        ...prevState,
        reservation: data,
        serviceType: reservationServiceType,
        isConfirmed: true,
        showDownloadBanner: true,
      }));
    } else if (data.party.has_open_spots) {
      setState((prevState) => ({
        ...prevState,
        reservation: data,
        serviceType: reservationServiceType,
        isPending: true,
        showDownloadBanner: true,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        reservation: data,
        serviceType: reservationServiceType,
        isNotFound: true,
        showDownloadButtons: true,
      }));
    }
    generatePageTitle(data?.venue?.name);

    getVenue({ id: data.venue.id }).then((response) => {
      // eslint-disable-next-line max-len
      const transformedVenueData = venueTransform(response);
      setState((prevState) => ({
        ...prevState,
        venueImage: transformedVenueData?.images?.[0],
        venueData: transformedVenueData,
      }));
    }).catch(() => {
      // no-op. Default to app-level metadata
    });
  };

  const updateRsvpStatuses = (status) => {
    setState((prevState) => ({
      ...prevState,
      alert: false,
      confirmProcessing: false,
      declineProcessing: false,
      isConfirmed: status === CONFIRMED,
      isDeclined: status === DECLINED,
      isPending: status === PENDING,
      isNotFound: status === NOTFOUND,
      showAuthModal: false,
      showDownloadBanner: status === CONFIRMED || status === PENDING,
      showDownloadButtons: status === DECLINED || status === NOTFOUND,
    }));
  };

  useEffect(() => {
    const inviteToken = $routeParams.token;

    const fetchInviteDetails = async () => {
      try {
        const response = await getPartyInviteDetails({ host_attendee_token: inviteToken });
        injectReservationData(response);
      } catch {
        updateRsvpStatuses(NOTFOUND);
      }
    };

    fetchInviteDetails();
  }, [$routeParams.token]);

  const setMetaData = () => {
    if (!venueData) {
      return;
    }

    const venueName = venueData.name;
    const title = getTranslation({
      translationKey: 'reservation_sheet_text_title',
      translationValues: { venueName },
      fallback: `RSVP to ${venueName} and add your dining preferences`,
    });
    const thumbnail = venueImage || undefined;

    $rootScope.metadata.description = venueData.metadata?.description || undefined;
    $rootScope.metadata.title = title || undefined;
    $rootScope.metadata.image = thumbnail;
    $rootScope.metadata.image_twitter = thumbnail;
    $rootScope.metadata.image_facebook = thumbnail;
  };

  useEffect(() => {
    setMetaData();
  }, [venueData, venueImage]);

  const toggleDeclineModal = () => {
    setState((prevState) => ({
      ...prevState,
      showDeclineModal: !prevState.showDeclineModal,
    }));
  };

  const renderLoader = () => <Loader />;

  // `skipAuthCheck` is passed by the auth modal's success callback: once the
  // user has just logged in or registered, we should accept the invite even
  // though the `isAuthenticated` prop captured in that callback's closure may
  // still be stale (CON-18705).
  const confirm = async ({ skipAuthCheck = false } = {}) => {
    const shouldBail = confirmProcessing === true || declineProcessing === true || reservation.party.current_user_confirmed;

    if (shouldBail) {
      return;
    }

    const inviteToken = $routeParams.token;

    // Opening the auth modal is not a loading state - only show the loader
    // while the acceptance request is actually in flight, otherwise the loader
    // hangs around for the entire lifetime of the signin/signup modal.
    if (!isAuthenticated && !skipAuthCheck) {
      setState((prevState) => ({ ...prevState, showAuthModal: true }));
      return;
    }

    Analytics.event('join invite', {
      venue: reservation.venue.name,
      city: reservation.venue.location.city,
    });

    setState((prevState) => ({ ...prevState, isLoading: true, confirmProcessing: true }));

    await authorizedConfirm(inviteToken);
  };

  const decline = async () => {
    toggleDeclineModal();

    if (confirmProcessing === true || declineProcessing === true) {
      return;
    }

    const inviteToken = $routeParams.token;

    setState((prevState) => ({ ...prevState, declineProcessing: true }));

    try {
      await postPartyInviteDecline(inviteToken);

      updateRsvpStatuses(DECLINED);
    } catch (error) {
      Logger.error(`[UserInvite] error removing from reservation: ${error?.data?.message}`);
      setState((prevState) => ({
        ...prevState,
        confirmProcessing: false,
        declineProcessing: false,
        alert: true,
      }));
    }
  };

  const renderAuthModal = () => {
    $rootScope.$broadcast('MODAL', 'signin', {
      successCallback: () => confirm({ skipAuthCheck: true }),
    });
  };

  const renderDeclineModal = () => (
    <Modal
      ariaHideApp={false}
      isOpen={showDeclineModal}
      onRequestClose={toggleDeclineModal}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      style={modalStyles}
      onAfterClose={focusTrap.focusPage}
      onAfterOpen={focusTrap.focusModal}
    >
      <div className="InviteModal__header">
        <TranslationProvider translationKey="invite_decline_confirm_header">
          <div className="InviteModal__title">Leave the reservation?</div>
        </TranslationProvider>
        <div
          className="InviteModal__close"
          onClick={toggleDeclineModal}
          onKeyDown={toggleDeclineModal}
          role="button"
          tabIndex={0}
        >
          <Icon name="IconClose" component={IconClose} background={false} />
        </div>
      </div>
      <div className="InviteModal__body">
        <TranslationProvider translationKey="invite_decline_confirm_body">
          <p>Are you sure you want to leave the reservation?</p>
        </TranslationProvider>
      </div>
      <div className="InviteModal__buttons">
        <TranslationProvider translationKey="invite_decline_confirm_yes">
          <Button
            className="InviteModal__buttons--decline"
            data-test-id={`${testId}-button-confirm_decline_invite`}
            onClick={decline}
            variant="primary"
          >
            Leave
          </Button>
        </TranslationProvider>
        <TranslationProvider
          translationKey="invite_decline_confirm_no"
        >
          <Button
            data-test-id={`${testId}-button-dismiss_decline_invite`}
            onClick={toggleDeclineModal}
            variant="secondary"
          >
            Cancel
          </Button>
        </TranslationProvider>
      </div>
    </Modal>
  );

  const seating = reservation.template ? reservation.template.seating : null;

  return (
    <>
      <div className="ResyInviteRsvp">
        {isLoading && renderLoader()}
        {showAuthModal && renderAuthModal()}
        {isPending
          && (
          <RSVPPending
            confirm={confirm}
            confirmProcessing={confirmProcessing}
            matchMedia={$rootScope.matchMedia}
            reservation={reservation}
            seating={seating}
            serviceType={serviceType}
            testId={testId}
            venueImage={venueImage}
          />
          )}
        {isConfirmed
          && (
          <RSVPConfirmed
            reservation={reservation}
            seating={seating}
            serviceType={serviceType}
            testId={testId}
            toggleDeclineModal={toggleDeclineModal}
          />
          )}
        {isDeclined && <RSVPDeclined />}
        {isNotFound && <RSVPNotFound />}
        {alert && renderAlert()}
        {showDeclineModal && renderDeclineModal()}
        {showDownloadButtons
          && (
            <div className="ResyInviteRsvp__download-btn-group">
              <div className="ResyInviteRsvp__download-btn">
                <Link href="https://itunes.apple.com/app/apple-store/id866163372?pt=86835890&ct=resycom&mt=8" target="_blank" rel="noopener noreferrer">
                  <Icon className="ResyIcon" component={DownloadAppleButton} iconClass="download-apple" />
                </Link>
              </div>
            </div>
          )}
      </div>

      {showDownloadBanner && <Download $translate={$translate} />}
    </>
  );
};

RSVP.propTypes = rsvpPropTypes;

const mapStateToProps = (state) => ({
  isAuthenticated: selectIsAuthenticated(state),
});

export { rsvpPropTypes, RSVP as ComponentUnderTest };
export default connect(mapStateToProps)(RSVP);
